import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "./gameUtils";

const NativeBoxScore = ({ data, sport }) => {
  const homePlayers = data?.box_score?.home || [];
  const awayPlayers = data?.box_score?.away || [];
  const sportLower = (sport || 'basketball').toLowerCase();

  const columns = sportLower.includes('football')
    ? [{ key: 'pass_yards', label: 'PASS' }, { key: 'rush_yards', label: 'RUSH' }, { key: 'rec_yards', label: 'REC' }]
    : [
        { key: 'points', label: 'PTS' }, { key: 'rebounds', label: 'REB' }, { key: 'assists', label: 'AST' },
        { key: 'steals', label: 'STL' }, { key: 'blocks', label: 'BLK' }, { key: 'fouls', label: 'PF' }
      ];

  const TeamTable = ({ players, teamName, isHome }) => (
    <div className="mb-6">
      <h4 className={`text-sm font-bold uppercase mb-2 px-2 py-1.5 rounded ${isHome ? 'bg-white/10 text-white' : 'bg-zinc-800 text-zinc-300'}`}>
        {teamName || (isHome ? 'Home' : 'Away')}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`boxscore-table-${isHome ? 'home' : 'away'}`}>
          <thead>
            <tr className="text-zinc-500 border-b border-white/10">
              <th className="text-left py-1.5 px-2 w-8">#</th>
              <th className="text-left py-1.5 px-2">Player</th>
              {columns.map(c => <th key={c.key} className="text-center py-1.5 px-2 w-10">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {players.filter(p => p.name && p.name.toLowerCase() !== 'totals' && p.name.toLowerCase() !== 'team')
              .sort((a, b) => (b.points || 0) - (a.points || 0))
              .map((p, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-1.5 px-2 text-zinc-500">{p.number || '-'}</td>
                  <td className="py-1.5 px-2 text-white font-medium truncate max-w-[140px]">{p.name}</td>
                  {columns.map(c => (
                    <td key={c.key} className="text-center py-1.5 px-2 text-zinc-300">{p[c.key] || 0}</td>
                  ))}
                </tr>
              ))}
            {players.length === 0 && (
              <tr><td colSpan={columns.length + 2} className="py-4 text-center text-zinc-500">No player data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div data-testid="native-boxscore">
      <TeamTable players={awayPlayers} teamName={data?.away_team} isHome={false} />
      <TeamTable players={homePlayers} teamName={data?.home_team} isHome={true} />
    </div>
  );
};

export const TabbedContent = ({ broadcast, boxScoreData }) => {
  const [activeTab, setActiveTab] = useState('recap');
  const [plays, setPlays] = useState([]);
  const [loadingPlays, setLoadingPlays] = useState(false);

  const tabs = [
    { id: 'recap', label: 'Recap' },
    { id: 'boxscore', label: 'Box Score' },
    { id: 'playbyplay', label: 'Play-by-Play' }
  ];

  useEffect(() => {
    if (activeTab === 'playbyplay' && broadcast?.stats_link && plays.length === 0) {
      setLoadingPlays(true);
      axios.get(`${API}/livestats/playbyplay`, {
        params: { url: broadcast.stats_link }
      })
        .then(res => {
          if (res.data?.plays) {
            setPlays(res.data.plays);
          }
        })
        .catch(err => console.error('Error fetching plays:', err))
        .finally(() => setLoadingPlays(false));
    }
  }, [activeTab, broadcast?.stats_link, plays.length]);

  const getPlayStyle = (play) => {
    const desc = (play.description || '').toLowerCase();
    if (play.is_scoring || desc.includes('made') && (desc.includes('3-pt') || desc.includes('dunk') || desc.includes('layup'))) {
      return 'border-l-4 border-green-500 bg-green-500/10';
    }
    if (desc.includes('turnover') || desc.includes('steal') || desc.includes('block')) {
      return 'border-l-4 border-red-500 bg-red-500/10';
    }
    if (desc.includes('foul')) {
      return 'border-l-4 border-yellow-500 bg-yellow-500/10';
    }
    if (desc.includes('timeout')) {
      return 'border-l-4 border-blue-500 bg-blue-500/10';
    }
    return 'border-l-4 border-zinc-600';
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10">
      <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 text-sm font-bold uppercase transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-orange-500 border-b-2 border-orange-500 bg-white/5'
                : 'text-zinc-500 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {activeTab === 'recap' && (
          <div className="text-zinc-400 text-sm">
            <p>Game recap content would appear here with highlights and key moments.</p>
          </div>
        )}

        {activeTab === 'boxscore' && (
          <div>
            {boxScoreData?.data?.iframe_url ? (
              <iframe
                src={boxScoreData.data.iframe_url}
                title="Box Score"
                className="w-full h-[500px] border-0 rounded"
                data-testid="boxscore-iframe"
              />
            ) : boxScoreData?.data?.box_score ? (
              <NativeBoxScore data={boxScoreData.data} sport={boxScoreData.sport} />
            ) : (
              <p className="text-zinc-500 text-sm text-center py-8">Box score data not available.</p>
            )}
          </div>
        )}

        {activeTab === 'playbyplay' && (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {loadingPlays ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : plays.length > 0 ? (
              <>
                {plays.map((play, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded ${getPlayStyle(play)}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {play.time && (
                            <span className="text-orange-500 text-xs font-mono">{play.time}</span>
                          )}
                          {play.team_name && (
                            <span className="text-zinc-500 text-xs">{play.team_name}</span>
                          )}
                        </div>
                        <p className="text-white text-sm">{play.description}</p>
                      </div>
                      {play.score && (
                        <span className="text-green-400 text-sm font-bold whitespace-nowrap">
                          {play.score}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : broadcast?.stats_link ? (
              <div className="text-center py-8">
                <p className="text-zinc-500 text-sm mb-4">Play-by-play data not available for this game.</p>
                <a
                  href={broadcast.stats_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 hover:text-orange-400 text-sm"
                >
                  View stats on provider site →
                </a>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm text-center py-8">No stats link configured for this game.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
